import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  ErrorHandler,
  inject,
  provideAppInitializer,
  EnvironmentProviders,
  makeEnvironmentProviders,
} from '@angular/core';
import { provideRouter, Router, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { provideHttpClient, withInterceptors, withFetch } from '@angular/common/http';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { Chart } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import * as Sentry from '@sentry/angular';

import { LUCIDE_ICONS, LucideIconData, LucideIconProvider } from 'lucide-angular';

import { routes } from './app.routes';
import { authHeader } from './interceptors/auth-header/auth-header';
import { sentinelHeaders } from './interceptors/sentinel-headers/sentinel-headers';
import { Auth } from './services/auth/auth';
import { lucideIcons } from './lucide-icons';
import { environment } from '../environments/environment';
import { ViewportScroller } from '@angular/common';
import { Posthog } from './services/posthog/posthog';
import { NeetoCal } from './services/neeto-cal/neeto-cal.service';

function provideAuth(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const auth = inject(Auth);
    return auth.init().catch((error) => {
      console.error('Clerk initialization failed:', error);
    });
  });
}

function provideChartConfiguration() {
  Chart.register(annotationPlugin, zoomPlugin);
  return provideCharts(withDefaultRegisterables());
}

function provideLucideIcons(icons: Record<string, LucideIconData>) {
  return {
    provide: LUCIDE_ICONS,
    multi: true,
    useValue: new LucideIconProvider(icons),
  };
}

function provideNeetoCal(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const neetoCal = inject(NeetoCal);
    neetoCal.initialize();
  });
}

function providePosthog(): EnvironmentProviders {
  return provideAppInitializer(() => {
    inject(Posthog);
  });
}

function provideSentryConfiguration(): EnvironmentProviders {
  if (!environment.sentryDsn) {
    return makeEnvironmentProviders([
      {
        provide: ErrorHandler,
        useClass: ErrorHandler,
      },
    ]);
  }

  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.sentryEnvironment,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: environment.production ? 0.2 : 1.0,
  });

  return makeEnvironmentProviders([
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    {
      provide: Sentry.TraceService,
      useFactory: () => new Sentry.TraceService(inject(Router)),
    },
  ]);
}

function provideViewportLayout(offset: [number, number]): EnvironmentProviders {
  return provideAppInitializer(() => {
    const viewport = inject(ViewportScroller);
    viewport.setOffset(offset);
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      withViewTransitions(),
    ),
    provideHttpClient(withInterceptors([authHeader, sentinelHeaders]), withFetch()),

    provideViewportLayout([0, 32]),
    provideLucideIcons(lucideIcons),
    provideChartConfiguration(),

    provideAuth(),
    providePosthog(),
    provideNeetoCal(),

    provideSentryConfiguration(),
  ],
};
